import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { SrIcon } from "@/components/ui/SrIcon";
import { matchesSearch } from "@/lib/equipment/filter";
import type { DBEquipmentItem } from "@/lib/db-types";

interface BookingItemSearchProps {
  /** Bookbart utstyr. Det disabledReason gir en grunn for, vises men kan ikke velges. */
  items: DBEquipmentItem[];
  departmentName: (id: string | null) => string;
  disabledReason?: (item: DBEquipmentItem) => string | null;
  onPick: (item: DBEquipmentItem) => void;
}

const MAX_HITS = 8;

/** Søk i utstyr og legg til en linje (mockup F). */
export function BookingItemSearch({ items, departmentName, disabledReason, onPick }: BookingItemSearchProps) {
  const [search, setSearch] = useState("");
  const hits = useMemo(
    () => (search.trim() ? items.filter((i) => matchesSearch(i, search)).slice(0, MAX_HITS) : []),
    [items, search],
  );

  return (
    <div className="relative">
      <SrIcon name="search" className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
      <Input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Søk i utstyr, f.eks. Komodo, Aputure, C-stand …"
        className="pl-8"
        aria-label="Søk i utstyr"
      />
      {hits.length > 0 && (
        <ul className="mt-1 w-full rounded-md border bg-popover max-h-64 overflow-auto" role="listbox" aria-label="Treff">
          {hits.map((i) => {
            const reason = disabledReason?.(i) ?? null;
            return (
              <li key={i.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={false}
                  aria-disabled={reason !== null}
                  disabled={reason !== null}
                  onClick={() => {
                    onPick(i);
                    setSearch("");
                  }}
                  className="flex w-full items-baseline justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-muted disabled:cursor-not-allowed disabled:hover:bg-transparent"
                >
                  <span className={reason ? "text-muted-foreground" : "font-medium"}>
                    {i.name}
                    {reason && <span className="block text-xs text-destructive">{reason}</span>}
                  </span>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {departmentName(i.department_id)}
                    {i.quantity > 1 ? ` · vi eier ${i.quantity}` : ""}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
